import React, { useEffect, useState, useRef, useMemo } from 'react';
import { PictureAsPdf, Close as CloseIcon } from '@mui/icons-material';
import {
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Chip
} from '@mui/material';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { getAllCandidates, getInProgressOnboardings, getCandidateStatusHistory } from '../services/onboardingapi';
import { getAllEmployees } from '../services/EmployeeService';
import { getAllInterviews } from '../services/interviewapi';
import { getGoals } from '../services/goalAPIs';
import { getAttendanceReport, getAttendanceSummary } from '../services/reportingAPIs';
import PageHeader from '../components/headers/Header';
import WeekAttendance from './Attendance';
import marabesLogo from '../assets/images/logos/Marabes.png';
import { formatHoursAsDays } from './helper';

// ✅ Helper: Load Remote Image
const getBase64FromUrl = async (url) => {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn("Could not load image for PDF:", url);
    return null;
  }
};

// === Constants ===
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#FF6B6B'];
const OFFICES = ['Nador', 'Tangier', 'Selouane'];
const TENURE_RANGES = [
  { label: '0-6 months', min: 0, max: 6 },
  { label: '6-12 months', min: 6, max: 12 },
  { label: '1-1.5 years', min: 12, max: 18 },
  { label: '1.5-2 years', min: 18, max: 24 },
  { label: '2-3 years', min: 24, max: 36 },
  { label: '+3 years', min: 36, max: Infinity },
];

// === Helper: Get position(s) for modal table (FTE ≥ 0.5, exclude Director, no FTE shown)
const getTablePositionDisplay = (employee) => {
  if (employee?.positions && Array.isArray(employee.positions)) {
    const validNames = employee.positions
      .filter(pos =>
        pos.fte != null &&
        pos.fte >= 0.5 &&
        pos.positionName &&
        pos.positionName.trim().toLowerCase() !== 'director'
      )
      .map(pos => pos.positionName.trim())
      .filter(name => name !== '');
    return validNames.length > 0 ? validNames.join('; ') : 'N/A';
  }
  // Fallback for old data
  if (employee?.position && employee.position.trim().toLowerCase() !== 'director') {
    return employee.position.trim();
  }
  return 'N/A';
};

// === Styles ===
const styles = {
  container: {
    minHeight: '100vh',
    padding: '16px',
    fontFamily: 'Arial, sans-serif',
  },
  header: {
    marginBottom: '32px',
    textAlign: 'center',
    width: '100%',
    paddingTop: '24px',
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  headerTitle: {
    fontSize: '2.25rem',
    fontWeight: 'bold',
    background: 'linear-gradient(135deg, #3d52a0 0%, #7091e6 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: '8px',
  },
  headerSubtitle: {
    fontSize: '1.25rem',
    color: '#6b7280',
  },
  kpiCards: { display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '24px' },
  kpiCard: {
    flex: '1 1 200px',
    borderRadius: '16px',
    padding: '20px',
    textAlign: 'center',
    boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
    color: 'white',
    fontWeight: 'bold',
  },
  kpiValue: { fontSize: '2.25rem', marginBottom: '8px' },
  kpiLabel: { fontSize: '1rem' },
  section: { marginBottom: '32px' },
  subsectionTitle: {
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#3d52a0',
    marginBottom: '16px',
    marginTop: '24px'
  },
  twoColumn: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
    marginBottom: '32px'
  },
  chartCard: {
    flex: '1 1 400px',
    backgroundColor: 'var(--card-bg, white)',
    borderRadius: '16px',
    padding: '16px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    marginBottom: '16px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '500px',
  },
  th: {
    backgroundColor: '#e3f2fd',
    padding: '10px',
    fontWeight: 'bold',
    textAlign: 'center',
    border: '1px solid #ddd',
  },
  td: {
    padding: '10px',
    textAlign: 'center',
    border: '1px solid #ddd',
  },
  footer: {
    marginTop: 'auto',
    textAlign: 'center',
    paddingTop: '24px',
    color: '#666',
    fontSize: '0.9rem',
  },
  modalHeader: {
    backgroundColor: '#f5f5f5',
    fontWeight: 'bold'
  },
  rowHover: {
    '&:hover': { backgroundColor: '#f5f5f5' }
  }
};

// === Helper Functions ===
const getWeekRangeFromISO = (weekString) => {
  if (!weekString) return { monday: null, sunday: null };
  const [year, week] = weekString.split('-W').map(Number);
  const jan4 = new Date(year, 0, 4);
  const daysOffset = 1 - jan4.getDay();
  const mondayOfWeek1 = new Date(jan4);
  mondayOfWeek1.setDate(jan4.getDate() + daysOffset);
  const monday = new Date(mondayOfWeek1);
  monday.setDate(mondayOfWeek1.getDate() + (week - 1) * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { monday, sunday };
};

const formatDate = (date) => {
  if (!date) return '';
  return `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getFullYear()}`;
};

const formatDateForBackend = (date) => {
  if (!date) return null;
  return new Date(date).toLocaleDateString('en-CA'); // YYYY-MM-DD
};

const isWithinMonthToDate = (dateString, sundayDate) => {
  if (!dateString || !sundayDate) return false;
  const date = new Date(dateString);
  const start = new Date(sundayDate.getFullYear(), sundayDate.getMonth(), 1);
  const end = new Date(sundayDate);
  end.setHours(23, 59, 59, 999);
  return date >= start && date <= end;
};

const isWithinWeek = (dateString, monday, sunday) => {
  if (!dateString || !monday || !sunday) return false;
  const date = new Date(dateString);
  return date >= monday && date <= sunday;
};

const normalizeOffice = (name) => name?.toLowerCase().replace(/\s+/g, '') || 'unknown';
const getStandardOffice = (name) => {
  const norm = normalizeOffice(name);
  if (norm === 'nador') return 'nador';
  if (['tanger', 'tangier'].includes(norm)) return 'tangier';
  if (norm === 'selouane') return 'selouane';
  return 'unknown';
};

const getMonthYear = (date) => date.toLocaleString('default', { month: 'long', year: 'numeric' });

const transformStatus = (apiStatus) => {
  if (typeof apiStatus === 'string') {
    const normalized = apiStatus.trim();
    const knownFrontend = [
      'Not Planned Yet', 'Not Attended', 'Pending',
      '1st Interview (Online)', '2nd Interview', '3rd Interview',
      'Hired', 'Declined by HR', 'Declined by Manager', 'Reserved List', 'Candidate Declined the Offer',
    ];
    if (knownFrontend.includes(normalized)) return normalized;
  }
  const map = {
    'NOT_PLANNED_YET': 'Not Planned Yet',
    'NOT_ATTENDED': 'Not Attended',
    'PENDING': 'Pending',
    'INTERVIEW_1': '1st Interview (Online)',
    'INTERVIEW_2': '2nd Interview', 'SECOND_INTERVIEW': '2nd Interview',
    'INTERVIEW_3': '3rd Interview', 'THIRD_INTERVIEW': '3rd Interview',
    'HIRED': 'Hired',
    'DECLINED_BY_HR': 'Declined by HR',
    'DECLINED_BY_MANAGER': 'Declined by Manager',
    'RESERVED_LIST': 'Reserved List',
    'CANDIDATE_DECLINED_THE_OFFER': 'Candidate Declined the Offer',
    'DECLINED_OFFER': 'Candidate Declined the Offer',
    'Candidate_Declined_the_Offre': 'Candidate Declined the Offer',
  };
  return map[apiStatus] || 'Not Planned Yet';
};

const formatLatenessHours = (hours) => {
  if (!hours || hours <= 0) return '0 min';
  if (hours >= 1) return `${hours.toFixed(1)} hrs`;
  return `${Math.round(hours * 60)} min`;
};

const getDepartmentName = (emp) => (emp.departmentName || emp.department?.name || '').trim().toLowerCase();
const isNotFacility = (emp) => getDepartmentName(emp) !== 'facility';



const HRWeeklyReport = () => {
  const reportRef = useRef();
  const mainHeaderRef = useRef();
  const employeeStatsRef = useRef();
  const attendanceRef = useRef();
  const recruitmentRef = useRef();

  const [candidates, setCandidates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [inProgressOnboardings, setInProgressOnboardings] = useState([]);
  const [goals, setGoals] = useState([]);
  const [candidateStatusHistory, setCandidateStatusHistory] = useState({}); // Map of candidateId -> status history
  const [loading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const jan4 = new Date(year, 0, 4);
    const days = Math.floor((now - jan4) / 86400000);
    const week = Math.ceil((days + jan4.getDay() + 1) / 7);
    return `${year}-W${week.toString().padStart(2, '0')}`;
  });
  const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('ALL');
  const [attendanceDetails, setAttendanceDetails] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const { monday, sunday } = useMemo(() => getWeekRangeFromISO(selectedWeek), [selectedWeek]);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const [
        candidatesRes,
        employeesRes,
        interviewsRes,
        onboardingsRes,
        goalsRes,
      ] = await Promise.all([
        getAllCandidates().catch(err => { console.error('Error fetching candidates:', err); return []; }),
        getAllEmployees().catch(err => { console.error('Error fetching employees:', err); return []; }),
        getAllInterviews().catch(err => { console.error('Error fetching interviews:', err); return []; }),
        getInProgressOnboardings().catch(err => { console.error('Error fetching onboardings:', err); return []; }),
        getGoals().catch(err => { console.error('Error fetching goals:', err); return []; }),
      ]);
      const transformCandidate = (c) => ({
        ...c,
        id: c.candidateId,
        status: transformStatus(c.status),
        applicationDate: c.applicationDate,
        createdDate: c.createdDate,
        lastModifiedDate: c.lastModifiedDate,
        office: c.office || 'Unknown',
      });
      const processedCandidates = (candidatesRes.data || candidatesRes || []).map(transformCandidate);

      // Only candidates in Hired or Declined statuses ever require status history for weekly reporting
      const TARGET_STATUSES = [
        'Hired',
        'Declined by HR',
        'Declined by Manager',
        'Candidate Declined the Offer'
      ];
      const candidatesNeedingHistory = processedCandidates.filter(c =>
        TARGET_STATUSES.includes(c.status)
      );

      // Fetch status history in small controlled batches (5 concurrent requests max)
      const statusHistoryMap = {};
      const batchSize = 5;
      for (let i = 0; i < candidatesNeedingHistory.length; i += batchSize) {
        const batch = candidatesNeedingHistory.slice(i, i + batchSize);
        await Promise.all(
          batch.map(async (candidate) => {
            try {
              const history = await getCandidateStatusHistory(candidate.id);
              statusHistoryMap[candidate.id] = history || [];
            } catch (error) {
              console.error(`Error fetching status history for candidate ${candidate.id}:`, error);
              statusHistoryMap[candidate.id] = [];
            }
          })
        );
      }

      setCandidates(processedCandidates);
      setEmployees(Array.isArray(employeesRes) ? employeesRes : (employeesRes.data || []));
      setInterviews(
        (Array.isArray(interviewsRes) ? interviewsRes : (interviewsRes.data || [])).map((i) => ({
          ...i,
          lastModifiedDate: i.lastModifiedDate || i.createdDate || new Date().toISOString(),
          status: i.status?.toUpperCase(),
        }))
      );
      setInProgressOnboardings(Array.isArray(onboardingsRes) ? onboardingsRes : (onboardingsRes.data || []));
      setGoals(goalsRes || []);
      setCandidateStatusHistory(statusHistoryMap);
    } catch (err) {
      console.error('Error fetching report data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, [selectedWeek]);

  // Helper function to check if candidate's last status change to target status occurred within date range
  const wasStatusChangedInRange = (candidateId, targetStatuses, startDate, endDate) => {
    const history = candidateStatusHistory[candidateId] || [];

    // Filter history to only include changes TO one of the target statuses
    const relevantStatusChanges = history.filter(h => {
      const transformedNewStatus = transformStatus(h.newStatus);
      return targetStatuses.includes(transformedNewStatus);
    });

    if (relevantStatusChanges.length === 0) return false;

    // Get the most recent status change to a target status
    const lastRelevantChange = relevantStatusChanges.reduce((latest, current) => {
      const currentDate = new Date(current.changeDate);
      const latestDate = new Date(latest.changeDate);
      return currentDate > latestDate ? current : latest;
    });

    // Check if this last status change occurred during the date range
    const changeDate = new Date(lastRelevantChange.changeDate);
    return changeDate >= startDate && changeDate <= endDate;
  };

  const handleViewAttendanceDetails = async (filterType = 'ALL') => {
    setActiveFilter(filterType);
    setAttendanceModalOpen(true);
    setLoadingDetails(true);
    try {
      const from = formatDateForBackend(monday);
      const to = formatDateForBackend(sunday);
      const data = await getAttendanceReport(from, to);
      setAttendanceDetails(data || []);
    } catch (error) {
      console.error("Failed to load attendance details", error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const filteredAttendance = useMemo(() => {
    if (activeFilter === 'ALL') return attendanceDetails.filter(emp =>
      (emp.totalSicknessRecords > 0) || (emp.totalLatenessRecords > 0) ||
      (emp.totalPersonalRecords > 0) || (emp.leaveDays?.ANNUAL_LEAVE > 0) ||
      (emp.leaveDays?.UNPAID_LEAVE > 0)
    );
    return attendanceDetails.filter(employee => {
      if (['ANNUAL_LEAVE', 'UNPAID_LEAVE'].includes(activeFilter)) return employee.leaveDays[activeFilter] > 0;
      if (activeFilter === 'SICKNESS') return employee.totalSicknessRecords > 0;
      if (activeFilter === 'LATENESS') return employee.totalLatenessRecords > 0;
      if (activeFilter === 'PERSONAL') return employee.totalPersonalRecords > 0;
      return false;
    });
  }, [attendanceDetails, activeFilter]);

  const getFilterLabel = (filter) => {
    const labels = {
      ALL: 'All Absence',
      ANNUAL_LEAVE: 'Annual Leave',
      UNPAID_LEAVE: 'Unpaid Leave',
      SICKNESS: 'Sickness',
      LATENESS: 'Lateness',
      PERSONAL: 'Personal'
    };
    return labels[filter] || filter;
  };

  // === Employee Statistics Logic ===
  const activeEmployeesThisWeek = employees.filter(emp => {
    if (emp.terminationDate) {
      // If termination date is in the future relative to this week, they are active
      // Usually we want active AT THE END of the week (sunday)
      if (new Date(emp.terminationDate) < sunday) return false;
    }
    const joinDate = new Date(emp.dateOfJoining || emp.createdDate);
    // Exclude Facility
    return !isNaN(joinDate.getTime()) && joinDate <= sunday && isNotFacility(emp);
  });
  const totalEmployees = activeEmployeesThisWeek.length;

  const deptMap = {};
  activeEmployeesThisWeek.forEach(e => {
    const dept = e.departmentName || e.department?.name || 'Unknown';
    deptMap[dept] = (deptMap[dept] || 0) + 1;
  });
  const deptData = Object.entries(deptMap).map(([name, value]) => ({ name, value }));

  // ✅ UPDATED: Exclude "Director", support multiple positions, FTE >= 0.5
  const posMap = {};
  activeEmployeesThisWeek.forEach(e => {
    if (e.positions && Array.isArray(e.positions)) {
      e.positions
        .filter(pos =>
          pos.fte != null &&
          pos.fte >= 0.5 &&
          pos.positionName &&
          pos.positionName.trim().toLowerCase() !== 'director'
        )
        .forEach(pos => {
          const name = pos.positionName.trim();
          if (name) {
            posMap[name] = (posMap[name] || 0) + 1;
          }
        });
    } else if (e.position && e.position.trim().toLowerCase() !== 'director') {
      // Fallback for legacy single position
      const name = e.position.trim();
      if (name) {
        posMap[name] = (posMap[name] || 0) + 1;
      }
    }
  });
  const posData = Object.entries(posMap)
    .filter(([name]) => name && name.toLowerCase() !== 'unknown')
    .map(([name, value]) => ({ name, value }));

  const officeMap = {};
  activeEmployeesThisWeek.forEach(e => {
    const office = getStandardOffice(e.officeName || e.office?.name) || 'Unknown';
    officeMap[office] = (officeMap[office] || 0) + 1;
  });
  const officeData = Object.entries(officeMap).map(([name, value]) => ({ name, value }));

  const tenureCounts = {};
  TENURE_RANGES.forEach(range => tenureCounts[range.label] = 0);
  const now = new Date();
  activeEmployeesThisWeek.forEach(e => {
    const joinDate = new Date(e.dateOfJoining || e.createdDate);
    if (isNaN(joinDate.getTime())) return;
    const diffTime = now - joinDate;
    const diffMonths = diffTime / (1000 * 60 * 60 * 24 * 30.44);
    const range = TENURE_RANGES.find(r => diffMonths >= r.min && diffMonths < r.max);
    if (range) tenureCounts[range.label]++;
  });
  const tenureData = TENURE_RANGES.map(range => ({ name: range.label, value: tenureCounts[range.label] }));

  // === Recruitment Logic (unchanged) ===
  const receivedApplications = candidates.filter(c =>
    c.applicationDate && isWithinMonthToDate(c.applicationDate, sunday)
  ).length;
  const hiredThisMonth = candidates.filter(c =>
    c.status === 'Hired' && wasStatusChangedInRange(c.id, ['Hired'], new Date(sunday.getFullYear(), sunday.getMonth(), 1), sunday)
  ).length;
  const declinedThisMonth = candidates.filter(c =>
    (c.status === 'Declined by HR' || c.status === 'Declined by Manager' || c.status === 'Candidate Declined the Offer') &&
    wasStatusChangedInRange(c.id, ['Declined by HR', 'Declined by Manager', 'Candidate Declined the Offer'], new Date(sunday.getFullYear(), sunday.getMonth(), 1), sunday)
  ).length;
  // Only count candidates from known offices (Nador, Tangier, Selouane)
  const notPlannedYet = candidates.filter(c => {
    const stdOffice = getStandardOffice(c.office);
    return c.status === 'Not Planned Yet' && ['nador', 'tangier', 'selouane'].includes(stdOffice);
  }).length;
  const applicationsReceivedThisWeek = candidates.filter(c =>
    c.applicationDate && isWithinWeek(c.applicationDate, monday, sunday)
  ).length;
  const interviewedThisWeek = interviews.filter(i =>
    i.status === 'COMPLETED' && i.lastModifiedDate && isWithinWeek(i.lastModifiedDate, monday, sunday)
  ).length;
  const plannedInterviewsThisWeek = interviews.filter(i =>
    i.status === 'SCHEDULED' && i.lastModifiedDate && isWithinWeek(i.lastModifiedDate, monday, sunday)
  ).length;
  const notAttendedThisWeek = interviews.filter(i =>
    i.status === 'NOT_ATTENDED' && i.lastModifiedDate && isWithinWeek(i.lastModifiedDate, monday, sunday)
  ).length;
  const declinedApplicationsThisWeek = candidates.filter(c =>
    (c.status === 'Declined by HR' || c.status === 'Declined by Manager') &&
    wasStatusChangedInRange(c.id, ['Declined by HR', 'Declined by Manager'], monday, sunday)
  ).length;
  const hiredThisWeek = candidates.filter(c =>
    c.status === 'Hired' && wasStatusChangedInRange(c.id, ['Hired'], monday, sunday)
  ).length;

  const pipelineOfficeRows = [
    { stage: 'Application Not planned yet', count: notPlannedYet },
    { stage: 'Applications Received', count: applicationsReceivedThisWeek },
    { stage: 'Interviewed this week', count: interviewedThisWeek },
    { stage: 'Planned Interviews', count: plannedInterviewsThisWeek },
    { stage: 'Not attended', count: notAttendedThisWeek },
    { stage: 'Declined Applications', count: declinedApplicationsThisWeek },
    { stage: 'Hired', count: hiredThisWeek },
  ].map(row => {
    let nador = 0, tangier = 0, selouane = 0;
    if (row.stage === 'Application Not planned yet') {
      nador = candidates.filter(c => c.status === 'Not Planned Yet' && getStandardOffice(c.office) === 'nador').length;
      tangier = candidates.filter(c => c.status === 'Not Planned Yet' && getStandardOffice(c.office) === 'tangier').length;
      selouane = candidates.filter(c => c.status === 'Not Planned Yet' && getStandardOffice(c.office) === 'selouane').length;
    } else if (row.stage === 'Applications Received') {
      nador = candidates.filter(c =>
        c.applicationDate && isWithinWeek(c.applicationDate, monday, sunday) && getStandardOffice(c.office) === 'nador'
      ).length;
      tangier = candidates.filter(c =>
        c.applicationDate && isWithinWeek(c.applicationDate, monday, sunday) && getStandardOffice(c.office) === 'tangier'
      ).length;
      selouane = candidates.filter(c =>
        c.applicationDate && isWithinWeek(c.applicationDate, monday, sunday) && getStandardOffice(c.office) === 'selouane'
      ).length;
    } else if (row.stage === 'Interviewed this week') {
      nador = candidates.filter(c => {
        const hasCompleted = interviews.some(i =>
          i.candidateId === c.id && i.status === 'COMPLETED' && i.lastModifiedDate && isWithinWeek(i.lastModifiedDate, monday, sunday)
        );
        return hasCompleted && getStandardOffice(c.office) === 'nador';
      }).length;
      tangier = candidates.filter(c => {
        const hasCompleted = interviews.some(i =>
          i.candidateId === c.id && i.status === 'COMPLETED' && i.lastModifiedDate && isWithinWeek(i.lastModifiedDate, monday, sunday)
        );
        return hasCompleted && getStandardOffice(c.office) === 'tangier';
      }).length;
      selouane = candidates.filter(c => {
        const hasCompleted = interviews.some(i =>
          i.candidateId === c.id && i.status === 'COMPLETED' && i.lastModifiedDate && isWithinWeek(i.lastModifiedDate, monday, sunday)
        );
        return hasCompleted && getStandardOffice(c.office) === 'selouane';
      }).length;
    } else if (row.stage === 'Planned Interviews') {
      nador = candidates.filter(c => {
        const hasPlanned = interviews.some(i =>
          i.candidateId === c.id && i.status === 'SCHEDULED' && i.lastModifiedDate && isWithinWeek(i.lastModifiedDate, monday, sunday)
        );
        return hasPlanned && getStandardOffice(c.office) === 'nador';
      }).length;
      tangier = candidates.filter(c => {
        const hasPlanned = interviews.some(i =>
          i.candidateId === c.id && i.status === 'SCHEDULED' && i.lastModifiedDate && isWithinWeek(i.lastModifiedDate, monday, sunday)
        );
        return hasPlanned && getStandardOffice(c.office) === 'tangier';
      }).length;
      selouane = candidates.filter(c => {
        const hasPlanned = interviews.some(i =>
          i.candidateId === c.id && i.status === 'SCHEDULED' && i.lastModifiedDate && isWithinWeek(i.lastModifiedDate, monday, sunday)
        );
        return hasPlanned && getStandardOffice(c.office) === 'selouane';
      }).length;
    } else if (row.stage === 'Not attended') {
      nador = candidates.filter(c => {
        const hasNotAttended = interviews.some(i =>
          i.candidateId === c.id && i.status === 'NOT_ATTENDED' && i.lastModifiedDate && isWithinWeek(i.lastModifiedDate, monday, sunday)
        );
        return hasNotAttended && getStandardOffice(c.office) === 'nador';
      }).length;
      tangier = candidates.filter(c => {
        const hasNotAttended = interviews.some(i =>
          i.candidateId === c.id && i.status === 'NOT_ATTENDED' && i.lastModifiedDate && isWithinWeek(i.lastModifiedDate, monday, sunday)
        );
        return hasNotAttended && getStandardOffice(c.office) === 'tangier';
      }).length;
      selouane = candidates.filter(c => {
        const hasNotAttended = interviews.some(i =>
          i.candidateId === c.id && i.status === 'NOT_ATTENDED' && i.lastModifiedDate && isWithinWeek(i.lastModifiedDate, monday, sunday)
        );
        return hasNotAttended && getStandardOffice(c.office) === 'selouane';
      }).length;
    } else if (row.stage === 'Declined Applications') {
      nador = candidates.filter(c =>
        (c.status === 'Declined by HR' || c.status === 'Declined by Manager' || c.status === 'Candidate Declined the Offer') &&
        wasStatusChangedInRange(c.id, ['Declined by HR', 'Declined by Manager', 'Candidate Declined the Offer'], monday, sunday) &&
        getStandardOffice(c.office) === 'nador'
      ).length;
      tangier = candidates.filter(c =>
        (c.status === 'Declined by HR' || c.status === 'Declined by Manager' || c.status === 'Candidate Declined the Offer') &&
        wasStatusChangedInRange(c.id, ['Declined by HR', 'Declined by Manager', 'Candidate Declined the Offer'], monday, sunday) &&
        getStandardOffice(c.office) === 'tangier'
      ).length;
      selouane = candidates.filter(c =>
        (c.status === 'Declined by HR' || c.status === 'Declined by Manager' || c.status === 'Candidate Declined the Offer') &&
        wasStatusChangedInRange(c.id, ['Declined by HR', 'Declined by Manager', 'Candidate Declined the Offer'], monday, sunday) &&
        getStandardOffice(c.office) === 'selouane'
      ).length;
    } else if (row.stage === 'Hired') {
      nador = candidates.filter(c =>
        c.status === 'Hired' &&
        wasStatusChangedInRange(c.id, ['Hired'], monday, sunday) &&
        getStandardOffice(c.office) === 'nador'
      ).length;
      tangier = candidates.filter(c =>
        c.status === 'Hired' &&
        wasStatusChangedInRange(c.id, ['Hired'], monday, sunday) &&
        getStandardOffice(c.office) === 'tangier'
      ).length;
      selouane = candidates.filter(c =>
        c.status === 'Hired' &&
        wasStatusChangedInRange(c.id, ['Hired'], monday, sunday) &&
        getStandardOffice(c.office) === 'selouane'
      ).length;
    }
    return { ...row, nador, tangier, selouane, total: nador + tangier + selouane };
  });

  const pieData = pipelineOfficeRows
    .filter(row => row.total > 0)
    .map(r => ({ name: r.stage, value: r.total }));

  const currentByOffice = {};
  const pendingByOffice = {};
  OFFICES.forEach(office => {
    const stdOffice = getStandardOffice(office);
    currentByOffice[office] = activeEmployeesThisWeek.filter(e =>
      getStandardOffice(e.officeName || e.office?.name) === stdOffice
    ).length;
    pendingByOffice[office] = inProgressOnboardings.filter(o =>
      getStandardOffice(o.candidate?.office) === stdOffice
    ).length;
  });

  const goalByOffice = {};
  OFFICES.forEach(office => {
    const officeGoalObj = goals.find(g => g.location?.name && getStandardOffice(g.location.name) === getStandardOffice(office) && !g.position);
    if (officeGoalObj) {
      goalByOffice[office] = officeGoalObj.scale;
    } else {
      goalByOffice[office] = currentByOffice[office] || 0;
    }
  });

  const workforceRows = OFFICES.map(office => {
    const current = currentByOffice[office] || 0;
    const pending = pendingByOffice[office] || 0;
    const goal = goalByOffice[office] || 0;
    const variance = current + pending - goal;
    return { office, current, pending, goal, variance };
  });

  const barData = OFFICES.map(office => ({
    office,
    'Current Employees': currentByOffice[office] || 0,
    'Hired (Start Date Pending)': pendingByOffice[office] || 0,
  })).filter(item => item['Current Employees'] > 0 || item['Hired (Start Date Pending)'] > 0);

  // === PDF Export ===
  const exportPDF = async () => {
    setIsExporting(true);
    const exportBtn = document.getElementById("no-print");
    if (exportBtn) exportBtn.style.display = "none";
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 5;
      const contentWidth = pageWidth - (margin * 2);
      const today = new Date().toLocaleDateString('en-GB');
      const axoLogoUrl = '/cLogo.png';
      const axoBase64 = await getBase64FromUrl(axoLogoUrl);
      try {
        doc.addImage(marabesLogo, 'PNG', margin, 10, 35, 12);
      } catch (e) {
        console.warn("Marabes logo failed", e);
      }
      if (axoBase64) {
        doc.addImage(axoBase64, 'PNG', pageWidth - margin - 35, 10, 35, 12);
      }
      doc.setDrawColor(63, 81, 181);
      doc.setLineWidth(0.5);
      doc.line(margin, 25, pageWidth - margin, 25);
      doc.setFillColor(232, 245, 255);
      doc.rect(margin, 30, contentWidth, 14, 'F');
      const textY = 39;
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      doc.text(`Date: ${today}`, margin + 3, textY);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 58, 139);
      doc.text('HR Weekly Report', pageWidth / 2, textY, { align: 'center' });

      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        ignoreElements: (element) => element.id === 'main-header-div'
      });
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = pageWidth - (margin * 2);
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const contentStartY = 50;
      if (imgHeight < (doc.internal.pageSize.getHeight() - contentStartY)) {
        doc.addImage(imgData, 'PNG', margin, contentStartY, imgWidth, imgHeight);
      } else {
        const fitHeight = doc.internal.pageSize.getHeight() - contentStartY - 10;
        doc.addImage(imgData, 'PNG', margin, contentStartY, imgWidth, fitHeight);
      }
      doc.save(`hr-weekly-report-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('PDF Error:', err);
      alert('Failed to generate PDF: ' + (err.message || 'Unknown error'));
    } finally {
      if (exportBtn) exportBtn.style.display = "block";
      setIsExporting(false);
    }
  };

  return (
    <div style={styles.container}>
      <div ref={reportRef}>
        <div id="main-header-div" ref={mainHeaderRef}>
          <div style={styles.header}>
            <div style={styles.headerTitle}>Weekly Report</div>
            <div style={styles.headerSubtitle}>
              Real-time insights and comprehensive reporting • {`Week: ${formatDate(monday)} to ${formatDate(sunday)}`}
            </div>
          </div>
        </div>

        <div id="no-print" className="no-print" style={{ marginBottom: '24px', backgroundColor: 'var(--card-bg, white)', padding: '16px', borderRadius: '16px', textAlign: 'center' }}>
          <div style={{ display: 'inline-flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type="week"
                value={selectedWeek}
                onChange={(e) => setSelectedWeek(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid var(--border-color, #e2e8f0)',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  minWidth: '160px',
                  minHeight: '40px',
                  backgroundColor: 'var(--bg-paper, #ffffff)',
                  color: 'var(--text-primary, #1e293b)'
                }}
              />
            </div>
            <button
              onClick={fetchReportData}
              disabled={loading}
              style={{ marginLeft: 'auto', padding: '8px 16px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', minHeight: '40px', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' }}
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
            <button
              onClick={exportPDF}
              disabled={loading || isExporting}
              style={{ cursor: 'pointer', padding: '8px 16px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', minHeight: '40px', fontSize: '0.875rem', fontWeight: 600 }}
            >
              <PictureAsPdf style={{ verticalAlign: 'middle', marginRight: '4px' }} />
              {isExporting ? 'Exporting...' : 'Export PDF'}
            </button>
          </div>
        </div>

        <div ref={employeeStatsRef}>
          <PageHeader title={`Employee Statistics (Total Employees ${totalEmployees} )`} fontSize={'1.25rem'} />
          <div style={styles.twoColumn}>
            <div style={styles.chartCard}>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  layout="vertical"
                  data={deptData}
                  margin={{ top: 20, right: 30, left: 70, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="name" />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" name="Employees by Department" fill="#667eea" label={{ position: 'right', fill: '#000' }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div style={styles.chartCard}>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  layout="vertical"
                  data={posData}
                  margin={{ top: 20, right: 30, left: 30, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="name" />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" fill="#43e97b" name="Employees by Position" label={{ position: 'right', fill: '#000' }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div style={styles.twoColumn}>
            <div style={styles.chartCard}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  layout="vertical"
                  data={tenureData}
                  margin={{ top: 20, right: 30, left: 30, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="name" />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" name="Employee Tenure" fill="#ff6b6b" label={{ position: 'right', fill: '#000' }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div style={styles.chartCard}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  layout="vertical"
                  data={officeData}
                  margin={{ top: 20, right: 30, left: 30, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="name" />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" name="Employees by Office Location" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <Divider sx={{ marginBottom: 2 }} />

        <div ref={attendanceRef} style={{ pageBreakInside: 'avoid', marginBottom: '30px' }}>
          <PageHeader title={'Attendance Statistics'} fontSize={'1.25rem'} />
          <WeekAttendance
            startDate={monday}
            endDate={sunday}
            loading={loading}
            number={2}
            sickN={2}
            onCardClick={handleViewAttendanceDetails}
          />
        </div>

        <Divider sx={{ marginBottom: 2 }} />

        <div ref={recruitmentRef}>
          <PageHeader title={'Recruitment Statistics'} fontSize={'1.25rem'} />
          <div style={{ marginBottom: '24px' }}>
            <div style={styles.subsectionTitle}>Monthly Recruitment Snapshot - {sunday ? getMonthYear(sunday) : ''}</div>
            <div style={styles.kpiCards}>
              <div style={{ ...styles.kpiCard, background: `linear-gradient(135deg, #6a5acd 0%, #3d52a0 100%)` }}>
                <div style={styles.kpiValue}>{receivedApplications}</div>
                <div style={styles.kpiLabel}>Received Applications</div>
              </div>
              <div style={{ ...styles.kpiCard, background: `linear-gradient(135deg, #1e88e5  0%, #6a5acd 100%)` }}>
                <div style={styles.kpiValue}>{hiredThisMonth}</div>
                <div style={styles.kpiLabel}>Hired This Month</div>
              </div>
              <div style={{ ...styles.kpiCard, background: `linear-gradient(135deg, #3d52a0 0%, #1e88e5 100%)` }}>
                <div style={styles.kpiValue}>{declinedThisMonth}</div>
                <div style={styles.kpiLabel}>Declined This Month</div>
              </div>
            </div>
          </div>
          <div style={styles.section}>
            <div style={styles.subsectionTitle}>Recruitment Status (Weekly Pipeline)</div>
            <div style={styles.twoColumn}>
              <div style={styles.chartCard}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Stage</th>
                      <th style={styles.th}>Nador</th>
                      <th style={styles.th}>Tangier</th>
                      <th style={styles.th}>Selouane</th>
                      <th style={styles.th}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pipelineOfficeRows.map((row, i) => (
                      <tr key={i}>
                        <td style={styles.td}>{row.stage}</td>
                        <td style={styles.td}>{row.nador}</td>
                        <td style={styles.td}>{row.tangier}</td>
                        <td style={styles.td}>{row.selouane}</td>
                        <td style={styles.td}>{row.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={styles.chartCard}>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                      isAnimationActive={false}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          <div style={styles.section}>
            <div style={styles.subsectionTitle}>Current Workforce Situation</div>
            <div style={styles.twoColumn}>
              <div style={styles.chartCard}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Office</th>
                      <th style={styles.th}>Current Employees</th>
                      <th style={styles.th}>Hired<br />(Start Date Pending)</th>
                      <th style={styles.th}>Goal</th>
                      <th style={styles.th}>Variance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workforceRows.map((row, i) => (
                      <tr key={i}>
                        <td style={styles.td}>{row.office}</td>
                        <td style={styles.td}>{row.current}</td>
                        <td style={styles.td}>{row.pending}</td>
                        <td style={styles.td}>{row.goal}</td>
                        <td style={{ ...styles.td, color: row.variance >= 0 ? '#2e7d32' : '#d32f2f' }}>
                          {row.variance >= 0 ? `+${row.variance}` : row.variance}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={styles.chartCard}>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={barData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="office" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Current Employees" fill="#667eea" label={{ position: 'top', fill: '#000', fontSize: 12, fontWeight: 'bold' }} isAnimationActive={false} />
                    <Bar dataKey="Hired (Start Date Pending)" fill="#43e97b" label={{ position: 'top', fill: '#000', fontSize: 12, fontWeight: 'bold' }} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={styles.footer}>
        Generated by Axopeople • {new Date().toLocaleString()}
      </div>

      {/* Attendance Details Modal */}
      <Dialog
        open={attendanceModalOpen}
        onClose={() => setAttendanceModalOpen(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="div">
            {getFilterLabel(activeFilter)} - ({formatDate(monday)} - {formatDate(sunday)})
          </Typography>
          <IconButton
            onClick={() => setAttendanceModalOpen(false)}
            sx={{ color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loadingDetails ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell rowSpan={2} sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Employee</TableCell>
                    <TableCell rowSpan={2} sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Department</TableCell>
                    <TableCell rowSpan={2} sx={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>Position</TableCell>
                    {['ANNUAL_LEAVE', 'ALL'].includes(activeFilter) && (
                      <TableCell align="center" rowSpan={2} sx={{ fontWeight: 'bold', backgroundColor: '#bbdefb' }}>Annual Leave (Days)</TableCell>
                    )}
                    {['UNPAID_LEAVE', 'ALL'].includes(activeFilter) && (
                      <TableCell align="center" rowSpan={2} sx={{ fontWeight: 'bold', backgroundColor: '#bbdefb' }}>Unpaid Leave (Days)</TableCell>
                    )}
                    {['SICKNESS', 'ALL'].includes(activeFilter) && (
                      <TableCell colSpan={2} align="center" sx={{ fontWeight: 'bold', backgroundColor: '#ffe0b2' }}>Sickness</TableCell>
                    )}
                    {['LATENESS', 'ALL'].includes(activeFilter) && (
                      <TableCell colSpan={1} align="center" sx={{ fontWeight: 'bold', backgroundColor: '#e1bee7' }}>Lateness</TableCell>
                    )}
                    {['PERSONAL', 'ALL'].includes(activeFilter) && (
                      <TableCell colSpan={2} align="center" sx={{ fontWeight: 'bold', backgroundColor: '#c8e6c9' }}>Personal</TableCell>
                    )}
                  </TableRow>
                  <TableRow>
                    {['SICKNESS', 'ALL'].includes(activeFilter) && <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'var(--warning-light, #fff3e0)', top: 36 }}>Rec</TableCell>}
                    {['SICKNESS', 'ALL'].includes(activeFilter) && <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'var(--warning-light, #fff3e0)', top: 36 }}>Hrs</TableCell>}
                    {['LATENESS', 'ALL'].includes(activeFilter) && <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f3e5f5', top: 36 }}>Rec</TableCell>}
                    {['PERSONAL', 'ALL'].includes(activeFilter) && <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#e8f5e9', top: 36 }}>Rec</TableCell>}
                    {['PERSONAL', 'ALL'].includes(activeFilter) && <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#e8f5e9', top: 36 }}>Hrs</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAttendance.length > 0 ? (
                    filteredAttendance.map((emp) => (
                      <TableRow key={emp.employeeId} sx={styles.rowHover}>
                        <TableCell>{emp.employeeName}</TableCell>
                        <TableCell>{emp.department || 'N/A'}</TableCell>
                        {/* ✅ UPDATED: Use new position helper */}
                        <TableCell>{getTablePositionDisplay(emp)}</TableCell>
                        {['ANNUAL_LEAVE', 'ALL'].includes(activeFilter) && (
                          <TableCell align="center" sx={{ backgroundColor: '#f7fbff' }}>
                            {emp.leaveDays?.ANNUAL_LEAVE > 0 ? <Chip label={emp.leaveDays.ANNUAL_LEAVE} color="primary" size="small" variant="outlined" /> : '-'}
                          </TableCell>
                        )}
                        {['UNPAID_LEAVE', 'ALL'].includes(activeFilter) && (
                          <TableCell align="center" sx={{ backgroundColor: '#f7fbff' }}>
                            {emp.leaveDays?.UNPAID_LEAVE > 0 ? <Chip label={emp.leaveDays.UNPAID_LEAVE} size="small" variant="outlined" /> : '-'}
                          </TableCell>
                        )}
                        {['SICKNESS', 'ALL'].includes(activeFilter) && (
                          <>
                            <TableCell align="center" sx={{ backgroundColor: 'var(--warning-lighter, #fffaf3)' }}>{emp.totalSicknessRecords || 0}</TableCell>
                            <TableCell align="center" sx={{ backgroundColor: 'var(--warning-lighter, #fffaf3)' }}>{Number(emp.totalSicknessHours || 0).toFixed(1)}</TableCell>
                          </>
                        )}
                        {['LATENESS', 'ALL'].includes(activeFilter) && (
                          <>
                            <TableCell align="center" sx={{ backgroundColor: '#faf5fb' }}>{emp.totalLatenessRecords || 0}</TableCell>
                          </>
                        )}
                        {['PERSONAL', 'ALL'].includes(activeFilter) && (
                          <>
                            <TableCell align="center" sx={{ backgroundColor: '#f5fbf6' }}>{emp.totalPersonalRecords || 0}</TableCell>
                            <TableCell align="center" sx={{ backgroundColor: '#f5fbf6' }}>{Number(emp.totalPersonalHours || 0).toFixed(1)}</TableCell>
                          </>
                        )}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={15} align="center" sx={{ py: 3 }}>
                        <Typography color="textSecondary">No records found for this category.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HRWeeklyReport;
